import {ApiClient} from '@hconnect/apiclient'
import {useMutation, useQueryClient, UseMutationOptions} from '@tanstack/react-query'
import {AxiosError} from 'axios'

import {EventsSearchQueryKey} from '../../events/hooks'
import {Comment, CommentDto} from '../../events/types'
import {mapComment} from '../mappers'
import {DeleteCommentParameter, EditCommentParameter} from '../types'

import {CommentsQueryKey} from './useComments'

type UseEditCommentParams = {
  plantId: string
  timezone: string
  isShiftComment?: boolean
  options?: UseMutationOptions<Comment, AxiosError, EditCommentParameter>
}

export const useEditComment = ({
  plantId,
  timezone,
  isShiftComment = false,
  options
}: UseEditCommentParams) => {
  const queryClient = useQueryClient()

  return useMutation<Comment, AxiosError, EditCommentParameter>({
    mutationFn: async ({commentId, description}) => {
      const path = isShiftComment
        ? `/shifthandover/${plantId}/shiftcomments/${commentId}`
        : `/shifthandover/${plantId}/comments/${commentId}`
      const payload = {
        description
      }

      const mutation = await ApiClient.axiosInstance.put<CommentDto>(path, payload)
      return mapComment(mutation.data, timezone)
    },
    ...options,
    onSuccess: async (data, variables, context, mutation) => {
      await queryClient.invalidateQueries({queryKey: [CommentsQueryKey]})
      await options?.onSuccess?.(data, variables, context, mutation)
    }
  })
}

type UseDeleteCommentParams = {
  plantId: string
  timezone: string
  isShiftComment?: boolean
  options?: UseMutationOptions<Comment, AxiosError, DeleteCommentParameter>
}

export const useDeleteComment = ({
  plantId,
  timezone,
  isShiftComment = false,
  options
}: UseDeleteCommentParams) => {
  const queryClient = useQueryClient()

  return useMutation<Comment, AxiosError, DeleteCommentParameter>({
    mutationFn: async ({commentId}) => {
      const path = isShiftComment
        ? `/shifthandover/${plantId}/shiftcomments/${commentId}`
        : `/shifthandover/${plantId}/comments/${commentId}`

      const mutation = await ApiClient.axiosInstance.delete<CommentDto>(path)
      return mapComment(mutation.data, timezone)
    },
    ...options,
    onSuccess: async (data, variables, context, mutation) => {
      await queryClient.invalidateQueries({queryKey: [CommentsQueryKey]})
      await queryClient.invalidateQueries({queryKey: [EventsSearchQueryKey]})
      await options?.onSuccess?.(data, variables, context, mutation)
    }
  })
}
